import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, List } from 'lucide-react';
import {
  MetricsRow, CompetencyChart, SubjectBreakdownPie,
  TopPerformersCard, AlertsCard, DrillCard,
} from '../../../components/LevelDashboard';
import type { SubjectBucket } from '../../../components/LevelDashboard';
import type { LevelMetrics, CompetencyBucket, TopPerformer, AlertItem } from '../../../types';
import {
  useGetKvsResultRoSummaryQuery,
  useGetKvsResultRoSubjectwiseQuery,
  useGetKvsResultRo3to5Query,
  useGetKvsResultRoCompetencyQuery,
  useGetKvsResultRoTopPerformersQuery,
  useGetKvsResult3to5RoDetailsQuery,
} from '../../../services/kvsResultApi';
import SchoolPassPercentTable from '../../../components/SchoolPassPercentTable';
import type { SchoolPassRow } from '../../../components/SchoolPassPercentTable';

const BASE = '/kvs-samagam/student-analytics/stages/preparatory';
const STAGE_CLASSES = [3, 4, 5];
const levelSlug = (level: string) => level.toLowerCase().replace(/\s+/g, '-');

const RegionDrillDown: React.FC = () => {
  const { regionId } = useParams<{ regionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  const termValue = searchParams.get('term') || 'overall';
  const term = termValue === 'overall' ? 0 : Number(termValue);
  const querySuffix = `?year=${academicYear}&term=${termValue}`;
  const numericRegionId = Number(regionId);

  const {
    data: summaryRes,
    isLoading: isSummaryLoading,
  } = useGetKvsResultRoSummaryQuery(
    !Number.isNaN(numericRegionId) ? { year: academicYear, term, roid: numericRegionId } : { year: academicYear, term, roid: 0 },
  );

  const {
    data: subjectRes,
    isLoading: isSubjectLoading,
  } = useGetKvsResultRoSubjectwiseQuery(
    !Number.isNaN(numericRegionId) ? { year: academicYear, term, roid: numericRegionId } : { year: academicYear, term, roid: 0 },
  );

  const {
    data: distRes,
    isLoading: isDistLoading,
  } = useGetKvsResultRo3to5Query(
    !Number.isNaN(numericRegionId) ? { year: academicYear, term, roid: numericRegionId } : { year: academicYear, term, roid: 0 },
  );

  const {
    data: competencyRes,
    isLoading: isCompetencyLoading,
  } = useGetKvsResultRoCompetencyQuery(
    !Number.isNaN(numericRegionId) ? { year: academicYear, term, roid: numericRegionId } : { year: academicYear, term, roid: 0 },
  );

  const {
    data: topRes,
    isLoading: isTopLoading,
  } = useGetKvsResultRoTopPerformersQuery(
    !Number.isNaN(numericRegionId)
      ? { year: academicYear, term, order: 'desc', roid: numericRegionId }
      : { year: academicYear, term, order: 'desc', roid: 0 },
  );

  const {
    data: bottomRes,
    isLoading: isBottomLoading,
  } = useGetKvsResultRoTopPerformersQuery(
    !Number.isNaN(numericRegionId)
      ? { year: academicYear, term, order: 'asc', roid: numericRegionId }
      : { year: academicYear, term, order: 'asc', roid: 0 },
  );

  const {
    data: roDetailsRes,
  } = useGetKvsResult3to5RoDetailsQuery(
    !Number.isNaN(numericRegionId) ? { year: academicYear, term, roid: numericRegionId } : { year: academicYear, term, roid: 0 },
  );

  const isLoading = isSummaryLoading || isSubjectLoading || isDistLoading || isCompetencyLoading || isTopLoading || isBottomLoading;

  const regionName = summaryRes?.data?.roName || `Region ${regionId}`;
  const schoolItems = competencyRes?.data?.schools || [];
  const passPctSchools = roDetailsRes?.data?.schools || [];

  const metrics: LevelMetrics = useMemo(() => {
    const data = summaryRes?.data;
    if (!data) {
      return {
        totalStudents: 0,
        avgAttendance: 0,
        avgCompetency: 0,
        needsSupportCount: 0,
        excellentCount: 0,
        avgBehaviorScore: 0,
        avgPeerScore: 0,
        bmiNormalPercent: 0,
      };
    }

    return {
      totalStudents: data.totalStudents,
      avgAttendance: 0,
      avgCompetency: +data.avgMarks.toFixed(1),
      needsSupportCount: data.needSupport,
      excellentCount: data.excellentStudents,
      avgBehaviorScore: 0,
      avgPeerScore: 0,
      bmiNormalPercent: 0,
    };
  }, [summaryRes]);

  const competencyBuckets: CompetencyBucket[] = useMemo(() => {
    const data = distRes?.data?.overall;
    if (!data) {
      return [
        { level: 'Excellent', count: 0, percent: 0 },
        { level: 'Good', count: 0, percent: 0 },
        { level: 'Average', count: 0, percent: 0 },
        { level: 'Below Average', count: 0, percent: 0 },
      ];
    }

    const total = data.total || 1;
    return [
      { level: 'Excellent', count: data.excellent, percent: +((data.excellent / total) * 100).toFixed(1) },
      { level: 'Good', count: data.good, percent: +((data.good / total) * 100).toFixed(1) },
      { level: 'Average', count: data.average, percent: +((data.average / total) * 100).toFixed(1) },
      { level: 'Below Average', count: data.belowAverage, percent: +((data.belowAverage / total) * 100).toFixed(1) },
    ];
  }, [distRes]);

  const subjectData: SubjectBucket[] = useMemo(() => {
    const items = subjectRes?.data?.overall || [];
    return items.map(item => ({ subject: item.subjectTitle, avgScore: +item.avgWeightage.toFixed(1) }));
  }, [subjectRes]);

  const topPerformers: TopPerformer[] = useMemo(() => {
    return (topRes?.data?.schools || []).slice(0, 5).map((school, index) => ({
      id: `top-${school.schoolID}-${index}`,
      name: school.schoolName,
      metric: 'Avg Marks',
      value: school.avgMarksPercentage,
    }));
  }, [topRes]);

  const alertItems: AlertItem[] = useMemo(() => {
    return (bottomRes?.data?.schools || []).slice(0, 5).map((school, index) => ({
      id: `alert-${school.schoolID}-${index}`,
      name: school.schoolName,
      issue: `Avg ${school.avgMarksPercentage}% — ${school.totalStudents.toLocaleString()} students`,
      severity: school.avgMarksPercentage < 60 ? 'high' : school.avgMarksPercentage < 75 ? 'medium' : 'low',
    }));
  }, [bottomRes]);

  if (!regionId || Number.isNaN(numericRegionId)) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Region not found.</p>
        <button onClick={() => navigate(-1)} className="text-indigo-600 text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  if (isLoading && !summaryRes) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <div className="text-sm text-gray-500">Loading region data…</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(location.pathname.slice(0, location.pathname.lastIndexOf(`/region/${regionId}`)) + location.search)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{regionName} Region</h1>
          <p className="text-sm text-gray-500">Preparatory Stage &mdash; Academic Year {academicYear} &mdash; {metrics.totalStudents.toLocaleString()} students &mdash; {schoolItems.length} schools</p>
        </div>
        <div className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
          <select
            id="term"
            value={termValue}
            onChange={(e) => navigate(`${location.pathname}?year=${academicYear}&term=${e.target.value}`)}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="overall">Overall</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
          </select>
        </div>
      </div>

      <MetricsRow m={metrics} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CompetencyChart buckets={competencyBuckets} onBarClick={(lvl) => navigate(`${BASE}/region/${regionId}/list/schools/${levelSlug(lvl)}${querySuffix}`)} />
        {subjectData.length > 0 && (
          <SubjectBreakdownPie data={subjectData} title="Subject-wise Avg Weightage" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersCard items={topPerformers} />
        <AlertsCard
          items={alertItems}
          onViewAll={() => navigate(`${BASE}/school-performance${querySuffix}`)}
          viewAllLabel="Need Attention Schools"
        />
      </div>

      <SchoolPassPercentTable
        schools={passPctSchools as SchoolPassRow[]}
        classes={STAGE_CLASSES}
        stageLabel="Preparatory Stage"
        regionName={regionName}
        year={academicYear}
        termLabel={termValue === 'overall' ? 'Overall' : `Term ${termValue}`}
        onRowClick={(s) => navigate(`${BASE}/region/${regionId}/school/${s.schoolID ?? s.kvID}${querySuffix}`)}
      />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Schools — Drill Down</h2>
          <button
            onClick={() => navigate(`${BASE}/region/${regionId}/list/schools${querySuffix}`)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition"
          >
            <List size={16} /> See All Schools
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {schoolItems.map((s) => (
            <DrillCard
              key={s.schoolID}
              title={s.schoolName}
              subtitle={`${s.totalStudents.toLocaleString()} students`}
              metrics={{
                totalStudents: s.totalStudents,
                avgAttendance: 0,
                avgCompetency: s.avgMarksPercentage,
                needsSupportCount: 0,
                excellentCount: 0,
                avgBehaviorScore: 0,
                avgPeerScore: 0,
                bmiNormalPercent: 0,
              }}
              alerts={[]}
              navigateTo={`school/${s.schoolID}${querySuffix}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RegionDrillDown;
